"use client";

import { useState } from "react";
import { Plus, Pencil, Trash2, Package } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  KIT_STATUSES,
  KIT_STATUS_LABELS,
  KIT_STATUS_OPTIONS,
  type KitStatus,
} from "@/lib/constants/kit-statuses";

export interface Kit {
  id: number;
  name: string;
  description: string | null;
  status: KitStatus;
  created_at?: string;
}

interface KitsPageClientProps {
  venueId: string;
  venueName: string;
  initialKits: Kit[];
  /** When true, the portal is read-only for this role (e.g. bar staff). */
  readOnly?: boolean;
  /** Base portal path, e.g. "/superadmin" or "/booking". */
  portalPath?: string;
}

interface KitFormState {
  name: string;
  description: string;
  status: KitStatus;
}

const emptyForm: KitFormState = {
  name: "",
  description: "",
  status: KIT_STATUSES.ACTIVE,
};

const statusBadgeClass: Record<KitStatus, string> = {
  active: "bg-green-100 text-green-800 border-green-200",
  inactive: "bg-gray-100 text-gray-700 border-gray-200",
  maintenance: "bg-amber-100 text-amber-800 border-amber-200",
};

export function KitsPageClient({
  venueId,
  venueName,
  initialKits,
  readOnly = false,
}: KitsPageClientProps) {
  const [kits, setKits] = useState<Kit[]>(initialKits);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingKit, setEditingKit] = useState<Kit | null>(null);
  const [deletingKit, setDeletingKit] = useState<Kit | null>(null);
  const [formData, setFormData] = useState<KitFormState>(emptyForm);
  const [isLoading, setIsLoading] = useState(false);

  const resetForm = () => setFormData(emptyForm);

  const refreshKits = async () => {
    try {
      const res = await fetch(`/api/venues/${venueId}/kits`);
      if (res.ok) {
        const data = await res.json();
        setKits(data.kits || []);
      }
    } catch (error) {
      console.error("Failed to refresh kits:", error);
    }
  };

  const handleAdd = async () => {
    if (!formData.name.trim()) return;
    setIsLoading(true);
    try {
      const res = await fetch(`/api/venues/${venueId}/kits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to create kit");
      }
      const result = await res.json();
      setIsAddOpen(false);
      resetForm();
      toast.success(result.message || "Kit created successfully");
      refreshKits();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to create kit",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const openEdit = (kit: Kit) => {
    setEditingKit(kit);
    setFormData({
      name: kit.name,
      description: kit.description || "",
      status: kit.status,
    });
  };

  const handleUpdate = async () => {
    if (!editingKit || !formData.name.trim()) return;
    setIsLoading(true);
    try {
      const res = await fetch(`/api/venues/${venueId}/kits/${editingKit.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to update kit");
      }
      const result = await res.json();
      setEditingKit(null);
      resetForm();
      toast.success(result.message || "Kit updated successfully");
      refreshKits();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update kit",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingKit) return;
    setIsLoading(true);
    try {
      const res = await fetch(`/api/venues/${venueId}/kits/${deletingKit.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to delete kit");
      }
      toast.success("Kit deleted successfully");
      setDeletingKit(null);
      refreshKits();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to delete kit",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const formFields = (
    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <Label htmlFor="kit-name">
          Name <span className="text-red-500">*</span>
        </Label>
        <Input
          id="kit-name"
          value={formData.name}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, name: e.target.value }))
          }
          placeholder="e.g., Game Show Kit 1"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="kit-description">Description</Label>
        <Textarea
          id="kit-description"
          value={formData.description}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, description: e.target.value }))
          }
          placeholder="Optional notes about this kit"
          rows={3}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="kit-status">Status</Label>
        <Select
          value={formData.status}
          onValueChange={(value) =>
            setFormData((prev) => ({ ...prev, status: value as KitStatus }))
          }
        >
          <SelectTrigger id="kit-status">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            {KIT_STATUS_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Kits</h1>
            <p className="text-gray-600 mt-2">
              Manage road-show equipment kits for {venueName}
            </p>
          </div>

          {!readOnly && (
            <Dialog
              open={isAddOpen}
              onOpenChange={(open) => {
                setIsAddOpen(open);
                if (!open) resetForm();
              }}
            >
              <DialogTrigger asChild>
                <Button onClick={() => resetForm()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Kit
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                  <DialogTitle>Add New Kit</DialogTitle>
                  <DialogDescription>
                    Create a new road-show kit for {venueName}.
                  </DialogDescription>
                </DialogHeader>
                {formFields}
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsAddOpen(false)}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAdd}
                    disabled={isLoading || !formData.name.trim()}
                  >
                    {isLoading ? "Adding..." : "Add Kit"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* List */}
        {kits.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
            <Package className="h-10 w-10 text-gray-400 mb-3" />
            <p className="text-gray-600">No kits yet.</p>
            {!readOnly && (
              <p className="text-sm text-gray-500 mt-1">
                Add a kit to make it available for road-show bookings.
              </p>
            )}
          </div>
        ) : (
          <div className="rounded-lg border bg-white overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="px-6">Name</TableHead>
                  <TableHead className="px-6">Description</TableHead>
                  <TableHead className="px-6">Status</TableHead>
                  {!readOnly && (
                    <TableHead className="px-6 text-right">Actions</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {kits.map((kit) => (
                  <TableRow key={kit.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium text-gray-900 px-6 py-4">
                      {kit.name}
                    </TableCell>
                    <TableCell className="text-gray-600 px-6 py-4">
                      {kit.description || "-"}
                    </TableCell>
                    <TableCell className="px-6 py-4">
                      <Badge
                        variant="secondary"
                        className={`text-xs ${statusBadgeClass[kit.status]}`}
                      >
                        {KIT_STATUS_LABELS[kit.status]}
                      </Badge>
                    </TableCell>
                    {!readOnly && (
                      <TableCell className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(kit)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeletingKit(kit)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Edit dialog */}
        <Dialog
          open={!!editingKit}
          onOpenChange={(open) => {
            if (!open) {
              setEditingKit(null);
              resetForm();
            }
          }}
        >
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Edit Kit</DialogTitle>
              <DialogDescription>
                Update information for "{editingKit?.name}".
              </DialogDescription>
            </DialogHeader>
            {formFields}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEditingKit(null)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdate}
                disabled={isLoading || !formData.name.trim()}
              >
                {isLoading ? "Updating..." : "Update Kit"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete confirmation */}
        <AlertDialog
          open={!!deletingKit}
          onOpenChange={(open) => !open && setDeletingKit(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete kit?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete "{deletingKit?.name}". This action
                cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={isLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                {isLoading ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

export default KitsPageClient;
